import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from "axios"

export default function MappingRice() {
    const [kakaoMap, setKakaoMap] = useState(null);
    const [locations, setLocations] = useState([]);
    const container = useRef();
    const [geoJsonData, setGeoJsonData] = useState({});
    const [currentLevel, setCurrentLevel] = useState(13);
    const polygonsRef = useRef([]);

    const currentYear = new Date().getFullYear();
    const startYear = 1990;
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // 지도 타입 변경 함수
    const setOverlayMapTypeId = () => {
        if (!kakaoMap) return;

        const mapTypes = {
            terrain: window.kakao.maps.MapTypeId.TERRAIN,
            roadmap: window.kakao.maps.MapTypeId.ROADMAP,
            useDistrict: window.kakao.maps.MapTypeId.USE_DISTRICT,
            hybrid: window.kakao.maps.MapTypeId.HYBRID,
        };

        const chkTerrain = document.getElementById('chkTerrain');
        const chkHybrid = document.getElementById('chkHybrid');
        const chkUseDistrict = document.getElementById('chkUseDistrict');

        for (const type in mapTypes) {
            kakaoMap.removeOverlayMapTypeId(mapTypes[type]);
        }

        if (chkUseDistrict.checked) kakaoMap.addOverlayMapTypeId(mapTypes.useDistrict);
        if (chkTerrain.checked) kakaoMap.addOverlayMapTypeId(mapTypes.terrain);

        if (chkHybrid.checked) {
            kakaoMap.setMapTypeId(mapTypes.hybrid);
        } else {
            kakaoMap.setMapTypeId(mapTypes.roadmap);
        }
    };

    // 백엔드에서 위치 데이터를 가져오는 함수
    const fetchLocationData = useCallback((year) => {
        const start = `${year}-01-01`;
        const end = `${year + 1}-12-31`; // status===0 대비 1년 더 조회

        axios.get('https://disease.scnu.ac.kr/riceblastlocation/', {
            params: { start, end }
        })
            .then(res => {
                const filteredData = res.data
                    .filter(location => {
                        const locationYear = new Date(location.date).getFullYear();
                        if (location.status === 1) {
                            return locationYear === year;        // status 1: 년도 그대로
                        } else if (location.status === 0) {
                            return locationYear - 1 === year;   // status 0: date년도가 year+1
                        }
                        return false;
                    })
                    .map(location => {
                        // ✅ status===0은 date를 1년 앞당겨 정규화
                        // getFillColorByDate에서 year와 정확히 매칭되도록
                        if (location.status === 0) {
                            const d = new Date(location.date);
                            d.setFullYear(d.getFullYear() - 1);
                            return { ...location, date: d.toISOString().split('T')[0] };
                        }
                        return location;
                    });

                setLocations(filteredData);
            })
            .catch(err => {
                console.error('위치 데이터를 가져오는 데 실패했습니다: ', err);
            });
    }, []);

    // selectedYear 변경 시 데이터 fetch
    useEffect(() => {
        fetchLocationData(selectedYear);
    }, [selectedYear, fetchLocationData]);

    // geojson 지역 구분 데이터 표시
    const fetchAllGeoJsonData = useCallback(() => {
        const url = './geojson/12_level.geojson';

        fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin'
        })
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok');
                return response.json();
            })
            .then(data => {
                setGeoJsonData({ level12: data });
            })
            .catch(error => console.error(`GeoJSON 데이터를 가져오는 데 실패했습니다: ${url}`, error));
    }, []);

    const clearPolygons = useCallback(() => {
        polygonsRef.current.forEach(polygon => polygon.setMap(null));
        polygonsRef.current = [];
    }, []);

    // selectedYear 기준으로 색상 결정
    // (status===0 데이터는 이미 date가 정규화되어 있으므로 단순 년도 비교 OK)
    const getFillColorByDate = (date, year) => {
        const locationYear = new Date(date).getFullYear();
        return locationYear === year ? "rgba(255,0,0,0.5)" : "rgba(255,255,255,1)";
    };

    const displayGeoJsonData = useCallback((level, locations, year) => {
        const data = geoJsonData.level12;
        const overlays = [];

        const clearOverlays = () => {
            overlays.forEach(overlay => overlay.setMap(null));
            overlays.length = 0;
        };

        if (!data || !kakaoMap) return;

        clearPolygons();

        const newPolygons = [];
        const groups = {};

        const groupKey = (groupName) => {
            const parts = groupName.split(' ');
            return parts[0] + ' ' + parts[1];
        };

        const displayPolygon = (polygonCoords, groupName, key) => {
            const paths = polygonCoords.map(ring =>
                ring.map(coord => new window.kakao.maps.LatLng(coord[1], coord[0]))
            );

            let fillColor = 'rgba(255,255,255,1)';
            const regionLocations = locations.filter(loc => loc.region === groupName);

            if (regionLocations.length) {
                const latest = regionLocations.reduce((acc, cur) =>
                    new Date(cur.date) > new Date(acc.date) ? cur : acc
                );
                fillColor = getFillColorByDate(latest.date, year);
            }

            const polygon = new window.kakao.maps.Polygon({
                map: kakaoMap,
                path: paths,
                strokeWeight: 2,
                strokeColor: '#000000',
                strokeOpacity: 0.4,
                strokeStyle: 'solid',
                fillColor: fillColor,
                fillOpacity: 0.3,
            });

            if (!groups[key]) groups[key] = [];
            groups[key].push(polygon);

            window.kakao.maps.event.addListener(polygon, 'mouseover', function (mouseEvent) {
                groups[key].forEach(p => p.setOptions({ fillColor: '#09f' }));

                const displayName = groupName.split(' ').slice(0, 2).join(' ');
                const content = `<div style="padding:2px; color: black; margin-bottom: 40px;"><p><b>${displayName}</b></p></div>`;
                const customOverlay = new window.kakao.maps.CustomOverlay({
                    content: content,
                    map: kakaoMap,
                    position: mouseEvent.latLng
                });

                overlays.push(customOverlay);
                polygon.customOverlay = customOverlay;
            });

            window.kakao.maps.event.addListener(polygon, 'mouseout', function () {
                groups[key].forEach(p => p.setOptions({ fillColor: fillColor }));
                if (polygon.customOverlay) {
                    polygon.customOverlay.setMap(null);
                }
            });

            window.kakao.maps.event.addListener(kakaoMap, 'zoom_changed', function () {
                clearOverlays();
            });

            newPolygons.push(polygon);
        };

        data.features.forEach(feature => {
            const coordinates = feature.geometry.coordinates;
            const type = feature.geometry.type;
            const groupName = feature.properties?.adm_nm || '이름 없음';
            const key = groupKey(groupName);

            if (type === "MultiPolygon") {
                coordinates.forEach(polygonCoords => {
                    displayPolygon(polygonCoords, groupName, key);
                });
            } else if (type === "Polygon") {
                displayPolygon(coordinates, groupName, key);
            }
        });

        polygonsRef.current = newPolygons;
    }, [geoJsonData, kakaoMap, clearPolygons]);

    // 카카오맵 초기화
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://dapi.kakao.com/v2/maps/sdk.js?appkey=c506977ead9cb4c4b588923984ff28cc&autoload=false';
        document.head.appendChild(script);

        script.onload = () => {
            window.kakao.maps.load(() => {
                const center = new window.kakao.maps.LatLng(36.6099, 127.70212);
                const options = { center, level: 12 };
                const map = new window.kakao.maps.Map(container.current, options);
                setKakaoMap(map);
                fetchAllGeoJsonData();
                map.setMapTypeId(window.kakao.maps.MapTypeId.ROADMAP);

                const zoomControl = new window.kakao.maps.ZoomControl();
                map.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);

                window.kakao.maps.event.addListener(map, 'zoom_changed', () => {
                    const newLevel = map.getLevel();
                    setCurrentLevel(newLevel);
                });
            });
        };

        return () => document.head.removeChild(script);
    }, [fetchAllGeoJsonData, fetchLocationData]);

    // locations, selectedYear, geoJsonData 변경 시 폴리곤 업데이트
    useEffect(() => {
        if (kakaoMap && Object.keys(geoJsonData).length > 0) {
            displayGeoJsonData(currentLevel, locations, selectedYear);
        }
    }, [currentLevel, kakaoMap, geoJsonData, displayGeoJsonData, locations, selectedYear]);

    const handlePreviousYear = () => {
        setSelectedYear(prevYear => Math.max(prevYear - 1, startYear));
    };

    const handleNextYear = () => {
        setSelectedYear(prevYear => Math.min(prevYear + 1, currentYear));
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px', marginTop: '20px' }}>
                {selectedYear > startYear && (
                    <button style={{ fontSize: '35px' }} onClick={handlePreviousYear} className='mdi mdi-arrow-left-bold-circle-outline'></button>
                )}
                <span style={{ margin: '0 10%', fontSize: '25px' }}>{selectedYear}</span>
                {selectedYear < currentYear && (
                    <button style={{ fontSize: '35px' }} onClick={handleNextYear} className='mdi mdi-arrow-right-bold-circle-outline'></button>
                )}
            </div>

            <div style={{ position: "relative", zIndex: "1" }}>
                <div style={{ marginBottom: '10px', marginLeft: '10%', marginTop: '10px' }}>
                    <div style={{ marginRight: '20px' }}>
                        <div style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: 'rgba(255,0,0,0.5)',
                            display: 'inline-block',
                            marginRight: '5px'
                        }}></div>
                        <span>해당 년도 발생</span>
                    </div>
                    <div style={{ marginRight: '10%', float: 'right' }}>
                        <label style={{ marginRight: '15px' }}><input type="checkbox" id="chkHybrid" onChange={setOverlayMapTypeId} /> 3D지도</label>
                        <label style={{ marginRight: '15px' }}><input type="checkbox" id="chkTerrain" onChange={setOverlayMapTypeId} /> 지형정보</label>
                        <label style={{ marginRight: '15px' }}><input type="checkbox" id="chkUseDistrict" onChange={setOverlayMapTypeId} /> 지적편집도정보</label>
                    </div>
                </div>
                <div
                    id="container"
                    ref={container}
                    style={{
                        width: "80%",
                        height: "850px",
                        marginLeft: "auto",
                        marginRight: "auto",
                        marginBottom: '3%',
                    }}
                />
            </div>
        </>
    );
}